// src/components/Exaustores/TimerPadraoBar/TimerPadraoBar.jsx
import React from "react";

/**
 * Barra de configuração do timer padrão
 * Timer só é atualizado na primeira requisição
 * Input com máscara HH:MM:SS e validação automática
 * 
 * @param {string} defaultTimerStr - String do timer (HH:MM:SS)
 * @param {Function} setDefaultTimerStr - Setter para atualizar string
 * @param {boolean} saving - Estado de salvamento
 * @param {string|null} saveMsg - Mensagem de feedback
 * @param {Function} onSave - Callback para salvar timer
 */
const TimerPadraoBar = ({
  defaultTimerStr,
  setDefaultTimerStr,
  saving,
  saveMsg,
  onSave,
}) => {
  /**
   * Formata string para máscara HH:MM:SS
   * Remove tudo exceto números e adiciona ":" automaticamente
   */
  const formatTimeInput = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 6 dígitos (HHMMSS)
    const limited = numbers.slice(0, 6);
    
    // Aplica a máscara progressivamente
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}:${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}:${limited.slice(2, 4)}:${limited.slice(4)}`;
    }
  };

  /**
   * Valida se o tempo está no range permitido
   * Mínimo: 00:00:01 | Máximo: 23:59:59
   */
  const validateTime = (timeStr) => {
    // Formato incompleto ainda é válido durante digitação
    if (timeStr.length < 8) return true;
    
    const parts = timeStr.split(':');
    if (parts.length !== 3) return false;
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    
    // Valida ranges
    if (hours < 0 || hours > 23) return false;
    if (minutes < 0 || minutes > 59) return false;
    if (seconds < 0 || seconds > 59) return false;
    
    // Não permite 00:00:00
    if (hours === 0 && minutes === 0 && seconds === 0) return false;
    
    return true;
  };

  /**
   * Handler para mudanças no input com máscara automática
   */
  const handleChange = (e) => {
    const formatted = formatTimeInput(e.target.value);
    
    // Apenas atualiza se for válido
    if (validateTime(formatted)) {
      setDefaultTimerStr(formatted);
    }
  };

  /**
   * Handler para tecla Enter
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Só salva se tiver formato completo (HH:MM:SS)
      if (defaultTimerStr.length === 8) {
        onSave();
      }
    }
    
    // Permite apenas números, backspace, delete, setas e tab
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Handler do botão Aplicar
   */
  const handleSaveClick = () => {
    // Só salva se tiver formato completo (HH:MM:SS)
    if (defaultTimerStr.length === 8) {
      onSave();
    }
  };

  /**
   * Handler para paste - limpa e formata o valor colado
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const formatted = formatTimeInput(pastedText);
    
    if (validateTime(formatted)) {
      setDefaultTimerStr(formatted);
    }
  };

  return (
    <div className="timerPadraoBar">
      <label htmlFor="timerPadraoInput">Temporização:</label>
      
      <input
        id="timerPadraoInput"
        className="timerPadraoInput"
        value={defaultTimerStr}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder="00:15:00"
        inputMode="numeric"
        autoComplete="off"
        maxLength={8}
      />
      
      <button
        className="timerPadraoBtn"
        disabled={saving || defaultTimerStr.length !== 8}
        onClick={handleSaveClick}
      >
        {saving ? "Salvando..." : "Aplicar"}
      </button>

      {saveMsg && <span className="timerPadraoMsg">{saveMsg}</span>}
    </div>
  );
};

export default TimerPadraoBar;